import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";

import { SegmentedInput } from "./join-ui.js";
import {
  createBrowserRoomProbe,
  discoveryForCandidate,
  queryRoomDirectory,
  revealRoom,
  RoomDirectoryThrottledError,
  RoomProbeError,
  RoomRevealCorrelationError,
  type OpaqueRoomCandidate,
  type RoomDiscoveryHttp,
  type RoomProbe,
  type RoomProbeFailureKind,
  type RoomQueryResult,
} from "./room-discovery.js";
import { strings } from "./strings.js";
import type { HostDiscovery } from "./discovery.js";

const CODE_FOCUS_KEY = "fallback-code";
const CODE_SUBMIT_FOCUS_KEY = "fallback-code-submit";
export const ROOM_DIRECTORY_REFRESH_MS = 45_000;
export const ROOM_DIRECTORY_THROTTLE_BACKOFF_MS = 5_000;
export const ROOM_DIRECTORY_THROTTLE_JITTER_MS = 1_000;

export type RoomRowStatus =
  | "opaque"
  | "checking"
  | "reachable"
  | "stale"
  | RoomProbeFailureKind;

export interface RoomRowView {
  readonly candidate: OpaqueRoomCandidate;
  readonly status: RoomRowStatus;
  readonly label?: string;
  readonly matchToken?: string;
}

export type RoomDirectoryView =
  | { readonly kind: "loading" }
  | { readonly kind: "empty" }
  | { readonly kind: "many" }
  | { readonly kind: "unavailable" }
  | { readonly kind: "rooms"; readonly rooms: readonly RoomRowView[] };

export type RoomDirectorySnapshot =
  | RoomQueryResult
  | { readonly kind: "unavailable" };

export interface SelectedRoomContext {
  readonly hostId: string;
  readonly label?: string;
  readonly matchToken: string;
}

export interface RoomListLandingProps {
  readonly apiBaseUrl?: string;
  readonly codeError?: string;
  readonly hostCode: string;
  readonly initialDirectory?: RoomDirectoryView;
  readonly onCodeChange: (value: string) => void;
  readonly onCodeSubmit: (event: FormEvent) => void;
  readonly onRoomSelected: (
    discovery: HostDiscovery,
    context: SelectedRoomContext,
  ) => void;
  readonly roomProbe?: RoomProbe;
  readonly http?: RoomDiscoveryHttp;
  readonly singleInputFallback: boolean;
}

export interface RoomDirectoryRefreshOptions {
  readonly refresh: () => Promise<void>;
  readonly intervalMs?: number;
  readonly setTimeout?: typeof globalThis.setTimeout;
  readonly clearTimeout?: typeof globalThis.clearTimeout;
  readonly random?: () => number;
}

export interface RoomSelectionLock {
  current: boolean;
}

export interface RunRoomSelectionOptions {
  readonly candidate: OpaqueRoomCandidate;
  readonly lock: RoomSelectionLock;
  readonly probe: RoomProbe;
  readonly apiBaseUrl?: string;
  readonly http?: RoomDiscoveryHttp;
  readonly onChecking: (hostId: string) => void;
  readonly onReachable: (
    hostId: string,
    context: Pick<SelectedRoomContext, "label" | "matchToken">,
  ) => void;
  readonly onBlocked: () => void;
  readonly onFailure: (hostId: string, status: RoomProbeFailureKind) => void;
  readonly onRoomSelected: RoomListLandingProps["onRoomSelected"];
}

export function RoomListLanding({
  apiBaseUrl,
  codeError,
  hostCode,
  initialDirectory,
  onCodeChange,
  onCodeSubmit,
  onRoomSelected,
  roomProbe,
  http,
  singleInputFallback,
}: RoomListLandingProps): React.JSX.Element {
  const [directory, setDirectory] = useState<RoomDirectoryView>(
    initialDirectory ?? { kind: "loading" },
  );
  const [activeFocusKey, setActiveFocusKey] = useState(CODE_FOCUS_KEY);
  const focusInsideRef = useRef(false);
  const latestSnapshotRef = useRef<RoomDirectorySnapshot | undefined>(
    undefined,
  );
  const probeActiveRef = useRef(false);
  const rootRef = useRef<HTMLElement>(null);
  const effectiveProbe = useMemo(
    () => roomProbe ?? createBrowserRoomProbe(),
    [roomProbe],
  );

  useEffect(() => {
    latestSnapshotRef.current = undefined;
    if (initialDirectory !== undefined) {
      setDirectory(initialDirectory);
      return;
    }
    if (apiBaseUrl === undefined) {
      latestSnapshotRef.current = { kind: "unavailable" };
      setDirectory({ kind: "unavailable" });
      return;
    }
    let cancelled = false;
    let firstRoomList = true;
    const refresh = async () => {
      try {
        const result = await queryRoomDirectory(apiBaseUrl, http);
        if (cancelled) return;
        latestSnapshotRef.current = result;
        const preserveFocusedRows = focusInsideRef.current;
        const selectInitialRoom = firstRoomList;
        if (result.kind === "rooms") firstRoomList = false;
        setDirectory((current) =>
          roomDirectoryAfterSnapshot(current, result, preserveFocusedRows),
        );
        if (!preserveFocusedRows) {
          setActiveFocusKey((current) =>
            roomFocusKeyAfterSnapshot(current, result, selectInitialRoom),
          );
        }
      } catch (error) {
        if (cancelled) return;
        if (error instanceof RoomDirectoryThrottledError) throw error;
        const unavailable = { kind: "unavailable" } as const;
        latestSnapshotRef.current = unavailable;
        const preserveFocusedRows = focusInsideRef.current;
        setDirectory((current) =>
          roomDirectoryAfterSnapshot(current, unavailable, preserveFocusedRows),
        );
        if (!preserveFocusedRows)
          setActiveFocusKey((current) =>
            roomFocusKeyAfterSnapshot(current, unavailable, false),
          );
      }
    };
    const stopRefreshing = startRoomDirectoryRefresh({ refresh });
    return () => {
      cancelled = true;
      stopRefreshing();
    };
  }, [apiBaseUrl, http, initialDirectory]);

  const selectRoom = async (candidate: OpaqueRoomCandidate) => {
    await runRoomSelection({
      candidate,
      lock: probeActiveRef,
      probe: effectiveProbe,
      ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
      ...(http === undefined ? {} : { http }),
      onChecking: (hostId) => {
        updateRoom(setDirectory, hostId, { status: "checking" });
      },
      onReachable: (hostId, context) => {
        updateRoom(setDirectory, hostId, {
          status: "reachable",
          ...(context.label === undefined ? {} : { label: context.label }),
          matchToken: context.matchToken,
        });
      },
      onBlocked: () => {
        setDirectory((current) =>
          current.kind === "rooms"
            ? {
                kind: "rooms",
                rooms: current.rooms.map((room) => ({
                  ...room,
                  status: "blocked",
                })),
              }
            : current,
        );
      },
      onFailure: (hostId, status) => {
        updateRoom(setDirectory, hostId, { status });
      },
      onRoomSelected,
    });
  };

  return (
    <section
      ref={rootRef}
      className="card landing room-list-landing join-checkpoint"
      aria-labelledby="room-list-heading"
      onFocusCapture={() => {
        focusInsideRef.current = true;
      }}
      onBlurCapture={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        focusInsideRef.current = false;
        const snapshot = latestSnapshotRef.current;
        if (snapshot === undefined) return;
        setDirectory((current) =>
          roomDirectoryAfterSnapshot(current, snapshot, false),
        );
        setActiveFocusKey((current) =>
          roomFocusKeyAfterSnapshot(current, snapshot, false),
        );
      }}
      onKeyDown={(event) =>
        moveRovingFocus(event, rootRef.current, setActiveFocusKey)
      }
    >
      <p className="eyebrow">{strings.roomDiscovery.eyebrow}</p>
      <h1 id="room-list-heading">{strings.roomDiscovery.heading}</h1>
      <p className="join-intro">{strings.roomDiscovery.intro}</p>

      <div className="room-directory" aria-live="polite">
        <RoomDirectoryMessage directory={directory} />
        {directory.kind === "rooms" && (
          <div className="room-list" role="list">
            {directory.rooms.map((room) => (
              <div key={room.candidate.hostId} role="listitem">
                <button
                  type="button"
                  className={`room-row room-row-${room.status}`}
                  data-room-focus-key={room.candidate.hostId}
                  tabIndex={activeFocusKey === room.candidate.hostId ? 0 : -1}
                  aria-busy={room.status === "checking"}
                  aria-disabled={room.status === "stale"}
                  aria-label={strings.roomDiscovery.joinRoom(
                    room.candidate.hostId,
                  )}
                  onFocus={() => setActiveFocusKey(room.candidate.hostId)}
                  onClick={() => {
                    if (room.status !== "stale")
                      void selectRoom(room.candidate);
                  }}
                >
                  <span className="room-status-dot" aria-hidden="true" />
                  <span className="room-row-copy">
                    <strong>
                      {room.label ??
                        strings.roomDiscovery.opaqueRoom(room.candidate.hostId)}
                    </strong>
                    <small>
                      {room.status === "reachable" && room.label === undefined
                        ? strings.roomDiscovery.nameUnavailable
                        : roomStatusDetail(room.status)}
                    </small>
                  </span>
                  <span className="room-status-chip">
                    {roomStatusLabel(room.status)}
                  </span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="room-fallbacks">
        <h2>{strings.roomDiscovery.fallbackHeading}</h2>
        <form onSubmit={onCodeSubmit} className="stack room-code-form">
          <div className="field-group">
            <label htmlFor="host-code">{strings.roomDiscovery.code}</label>
            <SegmentedInput
              id="host-code"
              autoCapitalize="characters"
              autoComplete="off"
              value={hostCode}
              onChange={(event) =>
                onCodeChange(event.target.value.toUpperCase())
              }
              placeholder="01ARZ3ND"
              length={8}
              mode="code"
              fallback={singleInputFallback}
              aria-describedby="host-code-hint"
              aria-invalid={codeError === undefined ? undefined : true}
              data-room-focus-key={CODE_FOCUS_KEY}
              tabIndex={activeFocusKey === CODE_FOCUS_KEY ? 0 : -1}
              onFocus={() => setActiveFocusKey(CODE_FOCUS_KEY)}
            />
            <p id="host-code-hint" className="field-hint">
              {strings.landing.hostCodeHint}
            </p>
          </div>
          {codeError !== undefined && (
            <p className="error-text" role="alert">
              {codeError}
            </p>
          )}
          <button
            type="submit"
            data-room-focus-key={CODE_SUBMIT_FOCUS_KEY}
            tabIndex={activeFocusKey === CODE_SUBMIT_FOCUS_KEY ? 0 : -1}
            onFocus={() => setActiveFocusKey(CODE_SUBMIT_FOCUS_KEY)}
          >
            {strings.landing.findBoard}
          </button>
        </form>
      </div>
    </section>
  );
}

export async function runRoomSelection({
  candidate,
  lock,
  probe,
  apiBaseUrl,
  http,
  onChecking,
  onReachable,
  onBlocked,
  onFailure,
  onRoomSelected,
}: RunRoomSelectionOptions): Promise<void> {
  if (lock.current) return;
  lock.current = true;
  onChecking(candidate.hostId);
  try {
    const proof = await probe.probe(candidate);
    let label: string | undefined;
    if (apiBaseUrl !== undefined) {
      try {
        const revealed = await revealRoom(apiBaseUrl, proof, http);
        label = revealed.label;
      } catch (error) {
        if (error instanceof RoomRevealCorrelationError) throw error;
        // Reachability remains authoritative and the registry is a soft
        // dependency. Keep the opaque host identity if reveal is unavailable.
      }
    }
    const context: SelectedRoomContext = {
      hostId: proof.hostId,
      ...(label === undefined ? {} : { label }),
      matchToken: proof.matchToken,
    };
    onReachable(candidate.hostId, context);
    onRoomSelected(discoveryForCandidate(candidate), context);
  } catch (error) {
    const status =
      error instanceof RoomProbeError ? error.kind : "registered-unreachable";
    if (status === "blocked") onBlocked();
    else onFailure(candidate.hostId, status);
  } finally {
    lock.current = false;
  }
}

export function roomStatusLabel(status: RoomRowStatus): string {
  switch (status) {
    case "checking":
      return strings.roomDiscovery.checking;
    case "reachable":
      return strings.roomDiscovery.reachable;
    case "stale":
      return strings.roomDiscovery.stale;
    case "guest-vlan":
      return strings.roomDiscovery.guestVlan;
    case "blocked":
      return strings.roomDiscovery.blocked;
    case "registered-unreachable":
      return strings.roomDiscovery.registeredUnreachable;
    case "opaque":
      return strings.roomDiscovery.select;
  }
}

export function roomStatusDetail(status: RoomRowStatus): string {
  switch (status) {
    case "guest-vlan":
      return strings.roomDiscovery.guestVlanDetail;
    case "blocked":
      return strings.roomDiscovery.blockedDetail;
    case "registered-unreachable":
      return strings.roomDiscovery.registeredUnreachableDetail;
    case "checking":
      return strings.roomDiscovery.checking;
    case "reachable":
      return strings.roomDiscovery.reachable;
    case "stale":
      return strings.roomDiscovery.staleDetail;
    case "opaque":
      return strings.roomDiscovery.opaqueDetail;
  }
}

export function selectedRoomDisclosureLabel(
  context: Pick<SelectedRoomContext, "label">,
): string {
  return context.label === undefined
    ? strings.roomDiscovery.nameUnavailable
    : strings.roomDiscovery.revealedLabel;
}

export function SelectedRoomProof({
  context,
}: {
  readonly context: SelectedRoomContext;
}): React.JSX.Element {
  return (
    <div className="selected-room-proof" role="status">
      <span>{selectedRoomDisclosureLabel(context)}</span>
      {context.label !== undefined && <strong>{context.label}</strong>}
    </div>
  );
}

function RoomDirectoryMessage({
  directory,
}: {
  readonly directory: RoomDirectoryView;
}): React.JSX.Element | null {
  switch (directory.kind) {
    case "loading":
      return (
        <div
          className="room-directory-message room-directory-loading"
          role="status"
        >
          <span className="room-directory-loading-label">
            <span className="connection-spinner" aria-hidden="true" />
            <span>{strings.roomDiscovery.loading}</span>
          </span>
          <span
            className="room-directory-skeleton-list"
            data-room-directory-skeleton="true"
            aria-hidden="true"
          >
            {[0, 1, 2].map((index) => (
              <span className="room-directory-skeleton-row" key={index}>
                <span className="room-directory-skeleton-copy" />
                <span className="room-directory-skeleton-chip" />
              </span>
            ))}
          </span>
        </div>
      );
    case "empty":
      return (
        <p className="room-directory-message">{strings.roomDiscovery.empty}</p>
      );
    case "many":
      return (
        <p className="room-directory-message room-directory-warning">
          {strings.roomDiscovery.many}
        </p>
      );
    case "unavailable":
      return (
        <p className="room-directory-message room-directory-warning">
          {strings.roomDiscovery.unavailable}
        </p>
      );
    case "rooms":
      return null;
  }
}

function updateRoom(
  setDirectory: React.Dispatch<React.SetStateAction<RoomDirectoryView>>,
  hostId: string,
  update: Pick<RoomRowView, "status"> &
    Partial<Pick<RoomRowView, "label" | "matchToken">>,
): void {
  setDirectory((current) => {
    if (current.kind !== "rooms") return current;
    return {
      kind: "rooms",
      rooms: roomRowsAfterUpdate(current.rooms, hostId, update),
    };
  });
}

export function roomRowsAfterUpdate(
  rooms: readonly RoomRowView[],
  hostId: string,
  update: Pick<RoomRowView, "status"> &
    Partial<Pick<RoomRowView, "label" | "matchToken">>,
): readonly RoomRowView[] {
  return rooms.map((room) =>
    room.candidate.hostId === hostId ? { ...room, ...update } : room,
  );
}

export function reconcileRoomRows(
  currentRooms: readonly RoomRowView[],
  candidates: readonly OpaqueRoomCandidate[],
  preserveFocusedRows: boolean,
): readonly RoomRowView[] {
  const currentByHost = new Map(
    currentRooms.map((room) => [room.candidate.hostId, room]),
  );
  const candidateByHost = new Map(
    candidates.map((candidate) => [candidate.hostId, candidate]),
  );

  if (!preserveFocusedRows) {
    return candidates.map((candidate) => {
      const current = currentByHost.get(candidate.hostId);
      return current === undefined
        ? { candidate, status: "opaque" }
        : {
            ...current,
            candidate,
            ...(current.status === "stale"
              ? { status: "opaque" as const }
              : {}),
          };
    });
  }

  const existingHostIds = new Set(
    currentRooms.map((room) => room.candidate.hostId),
  );
  const preserved = currentRooms.map((room) => {
    const candidate = candidateByHost.get(room.candidate.hostId);
    return candidate === undefined
      ? { ...room, status: "stale" as const }
      : {
          ...room,
          candidate,
          ...(room.status === "stale" ? { status: "opaque" as const } : {}),
        };
  });
  const appended = candidates
    .filter((candidate) => !existingHostIds.has(candidate.hostId))
    .map((candidate) => ({ candidate, status: "opaque" as const }));
  return [...preserved, ...appended];
}

export function roomDirectoryAfterSnapshot(
  current: RoomDirectoryView,
  snapshot: RoomDirectorySnapshot,
  preserveFocusedRows: boolean,
): RoomDirectoryView {
  if (snapshot.kind === "many" || snapshot.kind === "unavailable") {
    return preserveFocusedRows && current.kind === "rooms"
      ? {
          kind: "rooms",
          rooms: current.rooms.map((room) => ({
            ...room,
            status: "stale" as const,
          })),
        }
      : snapshot;
  }
  if (snapshot.candidates.length === 0) {
    return preserveFocusedRows && current.kind === "rooms"
      ? {
          kind: "rooms",
          rooms: reconcileRoomRows(current.rooms, [], true),
        }
      : { kind: "empty" };
  }
  return {
    kind: "rooms",
    rooms: reconcileRoomRows(
      current.kind === "rooms" ? current.rooms : [],
      snapshot.candidates,
      preserveFocusedRows,
    ),
  };
}

export function roomFocusKeyAfterSnapshot(
  currentKey: string,
  snapshot: RoomDirectorySnapshot,
  preferFirstCandidate: boolean,
): string {
  if (snapshot.kind !== "rooms" || snapshot.candidates.length === 0)
    return CODE_FOCUS_KEY;
  if (
    !preferFirstCandidate &&
    (isFallbackFocusKey(currentKey) ||
      snapshot.candidates.some((candidate) => candidate.hostId === currentKey))
  ) {
    return currentKey;
  }
  return snapshot.candidates[0]?.hostId ?? CODE_FOCUS_KEY;
}

export function startRoomDirectoryRefresh({
  refresh,
  intervalMs = ROOM_DIRECTORY_REFRESH_MS,
  setTimeout: schedule = globalThis.setTimeout,
  clearTimeout: cancel = globalThis.clearTimeout,
  random = Math.random,
}: RoomDirectoryRefreshOptions): () => void {
  if (!Number.isInteger(intervalMs) || intervalMs <= 0)
    throw new Error("room-directory refresh interval must be positive");
  let stopped = false;
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined;

  const tick = async () => {
    let delayMs = intervalMs;
    try {
      await refresh();
    } catch (error) {
      if (error instanceof RoomDirectoryThrottledError) {
        const jitter = Math.floor(
          Math.max(0, Math.min(1, random())) *
            ROOM_DIRECTORY_THROTTLE_JITTER_MS,
        );
        delayMs = ROOM_DIRECTORY_THROTTLE_BACKOFF_MS + jitter;
      }
      // The view's refresh callback owns its fail-closed presentation. Keep the
      // bounded heartbeat alive after an unexpected callback failure.
    }
    if (stopped) return;
    timer = schedule(() => {
      timer = undefined;
      void tick();
    }, delayMs);
  };

  void tick();
  return () => {
    stopped = true;
    if (timer !== undefined) cancel(timer);
    timer = undefined;
  };
}

export function nextRovingFocusKey(
  keys: readonly string[],
  currentKey: string | undefined,
  key: string,
): string | undefined {
  if (keys.length === 0) return undefined;
  if (key === "Home") return keys[0];
  if (key === "End") return keys[keys.length - 1];
  const direction =
    key === "ArrowDown" || key === "ArrowRight"
      ? 1
      : key === "ArrowUp" || key === "ArrowLeft"
        ? -1
        : 0;
  if (direction === 0) return undefined;
  const currentIndex = currentKey === undefined ? -1 : keys.indexOf(currentKey);
  const nextIndex =
    currentIndex < 0
      ? 0
      : (currentIndex + direction + keys.length) % keys.length;
  return keys[nextIndex];
}

function moveRovingFocus(
  event: KeyboardEvent<HTMLElement>,
  root: HTMLElement | null,
  setActiveFocusKey: (key: string) => void,
): void {
  if (root === null) return;
  const target = event.target;
  const horizontalInput =
    target instanceof HTMLInputElement &&
    (event.key === "ArrowLeft" || event.key === "ArrowRight");
  if (horizontalInput) return;
  const items = Array.from(
    root.querySelectorAll<HTMLElement>("[data-room-focus-key]"),
  );
  if (items.length === 0) return;
  const current = items.find((item) => item === target);
  const keys = items.flatMap((item) => {
    const focusKey = item.dataset.roomFocusKey;
    return focusKey === undefined ? [] : [focusKey];
  });
  const nextKey = nextRovingFocusKey(
    keys,
    current?.dataset.roomFocusKey,
    event.key,
  );
  if (nextKey === undefined) return;
  const next = items.find((item) => item.dataset.roomFocusKey === nextKey);
  if (next === undefined) return;
  event.preventDefault();
  setActiveFocusKey(nextKey);
  next.focus();
}

function isFallbackFocusKey(key: string): boolean {
  return key === CODE_FOCUS_KEY || key === CODE_SUBMIT_FOCUS_KEY;
}
